import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Chat from './Chat';
import CustomerModal from '../components/CustomerModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './Conversations.css';
import { 
  fetchConversationList, 
  fetchUnreadCount,
  createConversation,
  deleteConversation,
  fetchCustomerByPhone
} from '../store/slices/conversationsSlice';
import {
  createCustomer,
  updateCustomer
} from '../store/slices/customersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
const Conversations = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [newCustomerData, setNewCustomerData] = useState(null);
  const { conversationList, loading, error } = useSelector(state => state.conversations);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchConversationList({ limit: 100 }))
      .unwrap()
      .catch(err => {
        toast.error(err?.message || 'Failed to fetch conversations');
      });
  }, [dispatch, customerId]);

  // Use Redux conversations state
  const conversations = conversationList || [];

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.phone.includes(searchQuery)
  );

  // Select conversation by ID (both id and customerId from backend are strings)
  const selectedConversation = customerId ? conversations.find(c => c.id === customerId || c.customerId === customerId) : null;
  const handleConversationClick = (id) => {
    navigate(`/conversations/${id}`);
  };
  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleEditCustomer = async (e, customer) => {
    e.stopPropagation();
    const phone = customer.phone || customer.phoneNumber;
    
    if (!phone) {
      toast.error('Phone number not found');
      return;
    }

    try {
      // Fetch full customer data by phone number
      const result = await dispatch(fetchCustomerByPhone(phone)).unwrap();
      console.log('Fetched customer for edit:', result);
      setEditingCustomer(result?.data || null);
      setIsModalOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to fetch customer details');
    }
  };

  const handleDeleteCustomer = (e, customer) => {
    e.stopPropagation();
    setCustomerToDelete(customer);
    setIsDeleteModalOpen(true);
  };

  const handleSaveCustomer = async (formData) => {
    try {
      if (editingCustomer) {
        // Update existing customer using customers API
        await dispatch(updateCustomer({ 
          id: editingCustomer._id, 
          data: formData 
        })).unwrap();
        toast.success('Customer updated successfully');
        // Refresh conversation list to show updated data
        dispatch(fetchConversationList({ limit: 100 }));
      } else {
        // Create new customer using customers API
        const result = await dispatch(createCustomer(formData)).unwrap();
        toast.success('Customer created successfully');
        
        // Extract customer data from response
        const createdCustomer = result?.data || result;
        const newCustomerId = createdCustomer?._id;
        
        if (newCustomerId) {
          // Store new customer data temporarily until list loads
          setNewCustomerData({
            id: newCustomerId,
            _id: newCustomerId,
            name: createdCustomer?.name || formData?.name,
            phone: createdCustomer?.phone || formData?.phone,
            status: createdCustomer?.status || 'active',
            avatar: createdCustomer?.avatar || ''
          });
          
          // Navigate to new customer conversation
          navigate(`/conversations/${newCustomerId}`);
          
          // Refresh conversation list
          dispatch(fetchConversationList({ limit: 100 }));
        }
      }
      setIsModalOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to save customer');
    }
  };

  const handleConfirmDelete = async () => {
    if (customerToDelete) {
      try {
        await dispatch(deleteConversation(customerToDelete._id)).unwrap();
        toast.success('Conversation deleted successfully');
        // If deleted conversation was selected, navigate away
        if (customerId && customerId === customerToDelete._id) {
          navigate('/conversations');
        }
        setIsDeleteModalOpen(false);
        setCustomerToDelete(null);
      } catch (err) {
        toast.error(err?.message || 'Failed to delete conversation');
      }
    }
  };

  // Get customer data for chat
  const getCustomerData = (id) => {
    // Check if this is a newly created customer (not yet in list)
    if (newCustomerData && newCustomerData.id === id) {
      return newCustomerData;
    }
    
    const conv = conversations.find(c => c.id === id);
    if (!conv) {
      // New customer accessed directly via URL — use the id as phone number
      return { id, name: id, phone: id, status: 'offline', avatar: '' };
    }
    return {
      id: conv.id,
      name: conv.name,
      phone: conv.phone,
      status: conv.status,
      avatar: conv.avatar
    };
  };

  return (
    <div className="conversations-layout">
      <div className="conversations-sidebar">
        <div className="conversations-header">
          <div className="conversations-header-top">
            <h2>Conversations</h2>
            <button className="new-chat-btn" onClick={handleAddCustomer} title="Add Customer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </div>
          <div className="conversations-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search or start new chat"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="conversations-search-input"
            />
          </div>
        </div>

        <div className="conversations-list">
          {filteredConversations.length === 0 ? (
            <div className="empty-conversations">
              <div className="empty-icon">💬</div>
              <p>No conversations found</p>
            </div>
          ) : (
            filteredConversations.map(conversation => (
              <div
                key={conversation.id}
                className={`conversation-item ${customerId && customerId === conversation.id ? 'active' : ''}`}
                onClick={() => handleConversationClick(conversation.id)}
              >
                <div className="conversation-avatar">
                  {conversation.avatar}
                  {conversation.status === 'online' && (
                    <span className="online-indicator"></span>
                  )}
                </div>
                <div className="conversation-content">
                  <div className="conversation-header">
                    <h3 className="conversation-name">{conversation.name}</h3>
                    <span className="conversation-time">{conversation.timestamp}</span>
                  </div>
                  <div className="conversation-footer">
                    <p className="conversation-message">{conversation.lastMessage}</p>
                    {conversation.unreadCount > 0 && (
                      <span className="conversation-unread">{conversation.unreadCount}</span>
                    )}
                  </div>
                </div>
                <div className="conversation-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="action-btn edit-btn"
                    onClick={(e) => handleEditCustomer(e, conversation)}
                    title="Edit Customer"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={(e) => handleDeleteCustomer(e, conversation)}
                    title="Delete Customer"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="conversations-chat">
        {customerId ? (
          <Chat customerId={customerId} customer={getCustomerData(customerId)} />
        ) : (
          <div className="no-chat-selected">
            <div className="no-chat-icon">💬</div>
            <h2>Select a conversation</h2>
            <p>Choose a conversation from the list to start chatting</p>
          </div>
        )}
      </div>

      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCustomer(null);
        }}
        onSave={handleSaveCustomer}
        customer={editingCustomer}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCustomerToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={customerToDelete?.name || ''}
      />
    </div>
  );
};

export default Conversations;

